import React, { useState } from 'react';
import { TravelAbilityLogo } from './TravelAbilityLogo';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { assessmentData, getTotalQuestions, calculateProfile } from '../data/assessmentData';
import { 
  ArrowRight, ArrowLeft, Lightbulb, Target, Brain, Rocket, CheckCircle2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectionIcons = { 1: Target, 2: Brain, 3: Rocket };
const sectionColors = { 1: '#3B82F6', 2: '#10B981', 3: '#FBBF24' };

export const Assessment = ({ onComplete }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [inventoryAnswers, setInventoryAnswers] = useState({});
  const [multiSelectAnswers, setMultiSelectAnswers] = useState([]);
  const [multiCheckAnswers, setMultiCheckAnswers] = useState([]);
  const [textAnswer, setTextAnswer] = useState('');
  const [compoundAnswers, setCompoundAnswers] = useState({});
  const [roleAnswer, setRoleAnswer] = useState(null);
  const [roleOtherText, setRoleOtherText] = useState('');
  const [showReveal, setShowReveal] = useState(false);

  const section = assessmentData.sections[currentSection];
  const question = section?.questions[currentQuestion];
  const totalQuestions = getTotalQuestions();
  const answeredCount = Object.keys(answers).length;
  const progressPercent = (answeredCount / totalQuestions) * 100;
  const SectionIcon = sectionIcons[section?.id] || Target;

  const getCurrentQuestionNumber = () => {
    let count = 0;
    for (let s = 0; s < currentSection; s++) {
      count += assessmentData.sections[s].questions.length;
    }
    return count + currentQuestion + 1;
  };

  const handleSingleSelect = (option) => {
    setCurrentAnswer(option);
    setShowReveal(true);
  };

  const handleMultiCheck = (optionId, isExclusive) => {
    setMultiCheckAnswers(prev => {
      if (isExclusive) {
        return prev.includes(optionId) ? [] : [optionId];
      }
      const filtered = prev.filter(id => {
        const opt = question.options.find(o => o.id === id);
        return !opt?.exclusive;
      });
      if (filtered.includes(optionId)) {
        return filtered.filter(id => id !== optionId);
      }
      return [...filtered, optionId];
    });
    setShowReveal(true);
  };

  const handleInventoryToggle = (optionId, checked, isExclusive) => {
    setInventoryAnswers(prev => {
      const updated = { ...prev };
      if (isExclusive && checked) {
        return { [optionId]: { checked: true } };
      }
      if (checked) {
        delete updated.none;
        updated[optionId] = { checked: true, value: '' };
      } else {
        delete updated[optionId];
      }
      return updated;
    });
    setShowReveal(true);
  };

  const handleInventoryInput = (optionId, value) => {
    setInventoryAnswers(prev => ({
      ...prev,
      [optionId]: { ...prev[optionId], value }
    }));
  };

  const handleMultiSelect = (optionId) => {
    setMultiSelectAnswers(prev => {
      if (prev.includes(optionId)) return prev.filter(id => id !== optionId);
      if (prev.length >= (question.maxSelect || 2)) return prev;
      return [...prev, optionId];
    });
    setShowReveal(true);
  };

  const canProceed = () => {
    switch (question?.type) {
      case 'single': return currentAnswer !== null;
      case 'text': {
        const minLen = question.minLength || 1;
        return textAnswer.trim().length >= minLen;
      }
      case 'inventory': return Object.keys(inventoryAnswers).length > 0;
      case 'multiselect': return multiSelectAnswers.length > 0;
      case 'multicheck': return multiCheckAnswers.length > 0;
      case 'compound': {
        const subQs = question.subQuestions || [];
        return subQs.every(sq => compoundAnswers[sq.id]);
      }
      case 'role': {
        if (!roleAnswer) return false;
        if (roleAnswer.id === 'other') return roleOtherText.trim().length > 0;
        return true;
      }
      default: return false;
    }
  };

  const saveAndGetAnswer = () => {
    let answerData;
    switch (question.type) {
      case 'single': answerData = currentAnswer; break;
      case 'text': answerData = { text: textAnswer }; break;
      case 'inventory': answerData = inventoryAnswers; break;
      case 'multiselect': answerData = { selections: multiSelectAnswers }; break;
      case 'multicheck': answerData = { selections: multiCheckAnswers }; break;
      case 'compound': answerData = { compound: compoundAnswers }; break;
      case 'role': answerData = { 
        role: roleAnswer.id === 'other' ? roleOtherText : roleAnswer.text,
        roleId: roleAnswer.id
      }; break;
      default: answerData = null;
    }
    const updated = { ...answers, [question.id]: answerData };
    setAnswers(updated);
    return updated;
  };

  const resetCurrentInputs = () => {
    setCurrentAnswer(null);
    setInventoryAnswers({});
    setMultiSelectAnswers([]);
    setMultiCheckAnswers([]);
    setTextAnswer('');
    setCompoundAnswers({});
    setRoleAnswer(null);
    setRoleOtherText('');
    setShowReveal(false);
  };

  const handleNext = async () => {
    const updatedAnswers = saveAndGetAnswer();
    resetCurrentInputs();
    window.scrollTo(0, 0);

    const isLastQ = currentQuestion >= section.questions.length - 1;
    const isLastS = currentSection >= assessmentData.sections.length - 1;

    if (isLastQ && isLastS) {
      const profile = calculateProfile(updatedAnswers);
      onComplete({ answers: updatedAnswers, profile: assessmentData.profiles[profile] });
    } else if (isLastQ) {
      setCurrentSection(p => p + 1);
      setCurrentQuestion(0);
    } else {
      setCurrentQuestion(p => p + 1);
    }
  };

  const handleBack = () => {
    window.scrollTo(0, 0);
    if (currentQuestion > 0) setCurrentQuestion(p => p - 1);
    else if (currentSection > 0) {
      setCurrentSection(p => p - 1);
      setCurrentQuestion(assessmentData.sections[currentSection - 1].questions.length - 1);
    }
    resetCurrentInputs();
  };

  if (!question) return null;

  // Flatten inventory options from categories
  const getInventoryOptions = () => {
    if (question.categories) return question.categories;
    if (question.options) return [{ label: null, options: question.options }];
    return [];
  };

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#111111]/95 backdrop-blur border-b border-[#333333]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <TravelAbilityLogo size="sm" />
              <span className="font-bold text-[#F3F4F6] hidden sm:inline">2030 Assessment</span>
            </div>
            <span className="text-sm text-[#9CA3AF]">
              Question {getCurrentQuestionNumber()} of {totalQuestions}
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#9CA3AF]">Section {section.id}: {section.name}</span>
              <span className="text-[#9CA3AF]">{Math.round(progressPercent)}%</span>
            </div>
            <div className="h-3 bg-[#333333] rounded-full overflow-hidden">
              <div className="h-full progress-fill transition-all duration-500" style={{ width: `${progressPercent}%` }} data-testid="progress-bar" />
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
        {/* Section Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ backgroundColor: `${sectionColors[section.id]}20`, border: `1px solid ${sectionColors[section.id]}` }}
            data-testid="section-badge"
          >
            <SectionIcon className="w-5 h-5" style={{ color: sectionColors[section.id] }} />
            <span style={{ color: sectionColors[section.id] }} className="font-semibold text-sm">{section.tagline}</span>
          </div>
        </div>

        {/* Question Card */}
        <Card className="bg-[#1A1A1A] border-2 border-[#333333] mb-6">
          <CardContent className="p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-[#F3F4F6] mb-2" data-testid="question-title">{question.question}</h2>
            <p className="text-[#9CA3AF] mb-2">{question.subtext}</p>
            {question.helpText && (
              <p className="text-sm text-[#FBBF24]/80 italic mb-6 p-3 bg-[#FBBF24]/5 rounded-lg border border-[#FBBF24]/20">
                {question.helpText}
              </p>
            )}
            {!question.helpText && <div className="mb-6" />}

            {/* Single Select */}
            {question.type === 'single' && (
              <div className="space-y-3">
                {question.options.map((option) => (
                  <button key={option.id} onClick={() => handleSingleSelect(option)}
                    className={`w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 ${
                      currentAnswer?.id === option.id ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#333333] bg-[#1A1A1A] hover:border-[#FBBF24] hover:-translate-y-0.5'
                    }`} data-testid={`option-${option.id}`}>
                    <div className="flex items-start gap-4">
                      <span className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                        currentAnswer?.id === option.id ? 'bg-[#10B981] text-[#111111]' : 'bg-[#333333] text-[#F3F4F6]'
                      }`}>{option.id}</span>
                      <span className="text-base sm:text-lg text-[#F3F4F6]">{option.text}</span>
                      {currentAnswer?.id === option.id && <CheckCircle2 className="w-6 h-6 text-[#10B981] flex-shrink-0 ml-auto" />}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Text Input */}
            {question.type === 'text' && (
              <div>
                <Textarea value={textAnswer} onChange={(e) => {
                  const val = question.maxLength ? e.target.value.slice(0, question.maxLength) : e.target.value;
                  setTextAnswer(val);
                  setShowReveal(val.length >= (question.minLength || 10));
                }}
                  placeholder={question.placeholder}
                  className="bg-[#111111] border-2 border-[#333333] text-[#F3F4F6] text-lg min-h-[150px] focus:border-[#FBBF24] focus:ring-[#FBBF24]"
                  data-testid="text-answer" />
                {(question.minLength || question.maxLength) && (
                  <p className="text-sm text-[#6B7280] mt-2 text-right">
                    {textAnswer.length}{question.maxLength ? ` / ${question.maxLength}` : ''} characters
                    {question.minLength && textAnswer.length < question.minLength && (
                      <span className="text-[#FBBF24] ml-2">(min {question.minLength})</span>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Multi-Check (Q5 disability types) */}
            {question.type === 'multicheck' && (
              <div className="space-y-3">
                {question.options.map((option) => (
                  <button key={option.id} onClick={() => handleMultiCheck(option.id, option.exclusive)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                      multiCheckAnswers.includes(option.id) ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#333333] bg-[#1A1A1A] hover:border-[#FBBF24]'
                    }`} data-testid={`multicheck-${option.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        multiCheckAnswers.includes(option.id) ? 'bg-[#10B981] border-[#10B981]' : 'border-[#333333]'
                      }`}>
                        {multiCheckAnswers.includes(option.id) && <CheckCircle2 className="w-4 h-4 text-[#111111]" />}
                      </div>
                      <span className="text-[#F3F4F6]">{option.text}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Categorized Inventory (Q7) */}
            {question.type === 'inventory' && (
              <div className="space-y-6">
                {getInventoryOptions().map((category, catIdx) => (
                  <div key={catIdx}>
                    {category.label && (
                      <h4 className="text-sm font-bold text-[#FBBF24] mb-3 uppercase tracking-wide">{category.label}</h4>
                    )}
                    <div className="space-y-3">
                      {category.options.map((option) => (
                        <div key={option.id}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            inventoryAnswers[option.id]?.checked ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#333333] bg-[#1A1A1A]'
                          }`}>
                          <div className="flex items-start gap-3">
                            <Checkbox id={option.id}
                              checked={inventoryAnswers[option.id]?.checked || false}
                              onCheckedChange={(checked) => handleInventoryToggle(option.id, checked, false)}
                              className="mt-1 border-[#333333] data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                              data-testid={`checkbox-${option.id}`} />
                            <div className="flex-1">
                              <label htmlFor={option.id} className="text-[#F3F4F6] cursor-pointer">{option.text}</label>
                              {option.hasInput && inventoryAnswers[option.id]?.checked && (
                                <Input type={option.inputType} placeholder={option.inputLabel}
                                  value={inventoryAnswers[option.id]?.value || ''}
                                  onChange={(e) => handleInventoryInput(option.id, e.target.value)}
                                  className="mt-2 bg-[#111111] border-[#333333] text-[#F3F4F6] h-10"
                                  data-testid={`input-${option.id}`} />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {/* None option */}
                {question.noneOption && (
                  <div className={`p-4 rounded-xl border-2 transition-all mt-4 ${
                    inventoryAnswers[question.noneOption.id]?.checked ? 'border-[#EF4444] bg-[#EF4444]/10' : 'border-[#333333] bg-[#1A1A1A]'
                  }`}>
                    <div className="flex items-start gap-3">
                      <Checkbox id={question.noneOption.id}
                        checked={inventoryAnswers[question.noneOption.id]?.checked || false}
                        onCheckedChange={(checked) => handleInventoryToggle(question.noneOption.id, checked, true)}
                        className="mt-1 border-[#333333] data-[state=checked]:bg-[#EF4444] data-[state=checked]:border-[#EF4444]"
                        data-testid={`checkbox-${question.noneOption.id}`} />
                      <label htmlFor={question.noneOption.id} className="text-[#9CA3AF] cursor-pointer">{question.noneOption.text}</label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Multi-Select (Q9) */}
            {question.type === 'multiselect' && (
              <div className="space-y-3">
                <p className="text-sm text-[#FBBF24] mb-4">Select up to {question.maxSelect || 2} options</p>
                {question.options.map((option) => (
                  <button key={option.id} onClick={() => handleMultiSelect(option.id)}
                    disabled={!multiSelectAnswers.includes(option.id) && multiSelectAnswers.length >= (question.maxSelect || 2)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      multiSelectAnswers.includes(option.id) ? 'border-[#10B981] bg-[#10B981]/10'
                        : multiSelectAnswers.length >= (question.maxSelect || 2) ? 'border-[#333333] bg-[#1A1A1A] opacity-50 cursor-not-allowed'
                        : 'border-[#333333] bg-[#1A1A1A] hover:border-[#FBBF24]'
                    }`} data-testid={`multiselect-${option.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        multiSelectAnswers.includes(option.id) ? 'bg-[#10B981] border-[#10B981]' : 'border-[#333333]'
                      }`}>
                        {multiSelectAnswers.includes(option.id) && <CheckCircle2 className="w-4 h-4 text-[#111111]" />}
                      </div>
                      <span className="text-[#F3F4F6]">{option.text}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Compound Question (Q3 - Bandwidth + Budget) */}
            {question.type === 'compound' && (
              <div className="space-y-8">
                {question.subQuestions.map((sq) => (
                  <div key={sq.id}>
                    <h4 className="text-base font-semibold text-[#F3F4F6] mb-4">{sq.label}</h4>
                    <div className="space-y-3">
                      {sq.options.map((option) => (
                        <button key={option.id}
                          onClick={() => { setCompoundAnswers(prev => ({ ...prev, [sq.id]: option })); setShowReveal(true); }}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                            compoundAnswers[sq.id]?.id === option.id ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#333333] bg-[#1A1A1A] hover:border-[#FBBF24]'
                          }`} data-testid={`compound-${sq.id}-${option.id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              compoundAnswers[sq.id]?.id === option.id ? 'bg-[#10B981] border-[#10B981]' : 'border-[#333333]'
                            }`}>
                              {compoundAnswers[sq.id]?.id === option.id && <CheckCircle2 className="w-4 h-4 text-[#111111]" />}
                            </div>
                            <span className="text-[#F3F4F6]">{option.text}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Role Question (Q3 - dropdown + Other) */}
            {question.type === 'role' && (
              <div className="space-y-3">
                {question.options.map((option) => (
                  <div key={option.id}>
                    <button
                      onClick={() => { setRoleAnswer(option); setShowReveal(true); }}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        roleAnswer?.id === option.id ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#333333] bg-[#1A1A1A] hover:border-[#FBBF24]'
                      }`} data-testid={`role-${option.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          roleAnswer?.id === option.id ? 'bg-[#10B981] border-[#10B981]' : 'border-[#333333]'
                        }`}>
                          {roleAnswer?.id === option.id && <CheckCircle2 className="w-4 h-4 text-[#111111]" />}
                        </div>
                        <span className="text-[#F3F4F6]">{option.text}</span>
                      </div>
                    </button>
                    {option.id === 'other' && roleAnswer?.id === 'other' && (
                      <Input
                        value={roleOtherText}
                        onChange={(e) => setRoleOtherText(e.target.value)}
                        placeholder="Enter your role / title..."
                        className="mt-2 ml-9 bg-[#111111] border-[#333333] text-[#F3F4F6] text-lg h-12 focus:border-[#FBBF24] focus:ring-[#FBBF24]"
                        data-testid="role-other-input"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {showReveal && question.reveal && (
          <Card className="mb-6 bg-[#1A1A1A] border-[#333333] border-l-4 border-l-[#FBBF24]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-[#FBBF24]/20">
                  <Lightbulb className="w-5 h-5 text-[#FBBF24]" />
                </div>
                <div>
                  <p className="font-semibold text-[#FBBF24] mb-1">What This Reveals</p>
                  <p className="text-[#9CA3AF]">{question.reveal}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between gap-4">
          <Button onClick={handleBack} disabled={currentSection === 0 && currentQuestion === 0}
            variant="outline" className="border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24] disabled:opacity-30"
            data-testid="back-btn">
            <ArrowLeft className="mr-2 h-5 w-5" /> Back
          </Button>
          <Button onClick={handleNext} disabled={!canProceed()}
            className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#111111] font-bold text-lg px-8 py-6 rounded-xl disabled:opacity-50"
            data-testid="next-btn">
            {currentSection >= assessmentData.sections.length - 1 && currentQuestion >= section.questions.length - 1 ? 'See Results' : 'Continue'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Assessment;
